import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useProfile } from "@/hooks/useAuth";
import { fetchCategories, fetchTags, fetchMembers } from "@/lib/queries";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, EyeOff, Lock, Pencil, CornerDownRight, AlertTriangle, RotateCcw, Users, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { toastError } from "@/lib/errors";
import { isAdjustCategory } from "@/lib/categories";
import { useAppStore } from "@/store/useAppStore";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/categories")({ component: CategoriesPage });

type ScopeKey = "household" | string; // "household" or member id

function CategoriesPage() {
  const { profile } = useProfile();
  const hh = profile?.household_id;
  const qc = useQueryClient();
  const catQ = useQuery({ queryKey: ["categories", hh], queryFn: () => fetchCategories(hh!), enabled: !!hh });
  const tagQ = useQuery({ queryKey: ["tags", hh], queryFn: () => fetchTags(hh!), enabled: !!hh });
  const memQ = useQuery({ queryKey: ["members", hh], queryFn: () => fetchMembers(hh!), enabled: !!hh });
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingTag, setSavingTag] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState<"income" | "expense">("expense");
  const [color, setColor] = useState("#94a3b8");
  const [icon, setIcon] = useState("Tag");
  const [scopeKey, setScopeKey] = useState<ScopeKey>("household");
  const [parentId, setParentId] = useState<string>("none");
  const [tagName, setTagName] = useState("");

  const memberMap = new Map((memQ.data ?? []).map((m) => [m.id, m]));
  const { viewMode } = useAppStore();
  const isCouple = (memQ.data?.length ?? 1) > 1;

  // Original visibility/owner of the category being edited (to detect cascade)
  const [origVisibility, setOrigVisibility] = useState<"shared" | "private" | null>(null);
  const [origOwnerId, setOrigOwnerId] = useState<string | null>(null);

  const [filterType, setFilterType] = useState<"all" | "income" | "expense">("all");
  const [filterLevel, setFilterLevel] = useState<"all" | "parent" | "child">("all");
  const [showArchived, setShowArchived] = useState(false);
  const [showSystemCats, setShowSystemCats] = useState(false);

  // Query para saber si el padre seleccionado tiene movimientos (warning primera subcategoría)
  const parentTxCountQ = useQuery({
    queryKey: ["category-tx-count", parentId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("transaction_lines")
        .select("id", { count: "exact", head: true })
        .eq("category_id", parentId);
      if (error) return 0;
      return count ?? 0;
    },
    enabled: open && parentId !== "none",
  });
  const archivedCatQ = useQuery({
    queryKey: ["categories-archived", hh],
    queryFn: async () => {
      const { data, error } = await supabase.from("categories").select("*").eq("household_id", hh!).eq("is_active", false).order("name");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!hh && showArchived,
  });
  const archivedCountQ = useQuery({
    queryKey: ["categories-archived-count", hh],
    queryFn: async () => {
      const { count } = await supabase.from("categories").select("id", { count: "exact", head: true }).eq("household_id", hh!).eq("is_active", false);
      return count ?? 0;
    },
    enabled: !!hh,
  });

  function handleScopeChange(v: string) {
    setScopeKey(v);
    setParentId("none"); // Si cambia el scope, el padre anterior puede no ser válido
  }

  function openNew() {
    setEditingId(null);
    setName(""); setType("expense"); setColor("#94a3b8"); setIcon("Tag");
    setScopeKey("household"); setParentId("none");
    setOrigVisibility(null); setOrigOwnerId(null);
    setOpen(true);
  }
  function openEdit(c: any) {
    setEditingId(c.id);
    setName(c.name); setType(c.type); setColor(c.color); setIcon(c.icon);
    setScopeKey(c.visibility === "private" && c.owner_id ? c.owner_id : "household");
    setParentId(c.parent_id ?? "none");
    setOrigVisibility(c.visibility);
    setOrigOwnerId(c.owner_id ?? null);
    setOpen(true);
  }

  async function saveCategory() {
    if (!hh) return;
    if (saving) return;
    if (!name.trim()) return toast.error("El nombre es obligatorio");
    const parent_id = parentId === "none" ? null : parentId;
    // Force inheritance from parent (defense in depth — DB trigger also enforces it)
    let visibility: "shared" | "private";
    let owner_id: string | null;
    if (parent_id) {
      const parent: any = cats.find((c: any) => c.id === parent_id);
      if (!parent) return toast.error("Categoría padre no encontrada");
      visibility = parent.visibility;
      owner_id = parent.owner_id ?? null;
    } else {
      const isHousehold = scopeKey === "household";
      visibility = isHousehold ? "shared" : "private";
      owner_id = isHousehold ? null : scopeKey;
    }
    setSaving(true);
    try {
      if (editingId) {
        const { error } = await supabase.from("categories").update({
          name: name.trim(), type, color, icon, visibility, owner_id, parent_id,
        }).eq("id", editingId);
        if (error) return toastError(error);
        toast.success("Categoría actualizada");
      } else {
        const { error } = await supabase.from("categories").insert({
          household_id: hh, name: name.trim(), type, color, icon, visibility, owner_id, parent_id,
        });
        if (error) return toastError(error);
        toast.success("Categoría creada");
      }
      qc.invalidateQueries({ queryKey: ["categories"] });
      setOpen(false);
    } finally {
      setSaving(false);
    }
  }
  async function deactivateCat(id: string) {
    await supabase.from("categories").update({ is_active: false }).eq("id", id);
    qc.invalidateQueries({ queryKey: ["categories"] });
    qc.invalidateQueries({ queryKey: ["categories-archived-count"] });
    toast.success("Categoría desactivada (el historial se conserva)");
  }
  async function reactivateCat(id: string) {
    await supabase.from("categories").update({ is_active: true }).eq("id", id);
    qc.invalidateQueries({ queryKey: ["categories"] });
    qc.invalidateQueries({ queryKey: ["categories-archived"] });
    qc.invalidateQueries({ queryKey: ["categories-archived-count"] });
    toast.success("Categoría reactivada");
  }
  async function addTag() {
    if (!hh || !tagName.trim()) return;
    if (savingTag) return;
    setSavingTag(true);
    try {
      await supabase.from("tags").insert({ household_id: hh, name: tagName.trim() });
      qc.invalidateQueries({ queryKey: ["tags"] });
      setTagName("");
    } finally {
      setSavingTag(false);
    }
  }
  async function deleteTag(id: string) {
    await supabase.from("tags").delete().eq("id", id);
    qc.invalidateQueries({ queryKey: ["tags"] });
  }

  const cats = catQ.data ?? [];
  const parentMap = new Map(cats.map((c: any) => [c.id, c]));
  // Categories that already have at least one child — cannot become subcategories themselves
  const hasChildrenSet = new Set<string>(
    cats.filter((c: any) => c.parent_id).map((c: any) => c.parent_id as string),
  );
  const editingIsParent = !!editingId && hasChildrenSet.has(editingId);
  const childCountOfEditing = editingId
    ? cats.filter((c: any) => c.parent_id === editingId).length
    : 0;
  function matchFilters(c: any): boolean {
    if (!showSystemCats && isAdjustCategory(c)) return false;
    if (filterType !== "all" && c.type !== filterType) return false;
    if (isCouple) {
      if (viewMode === "household" && c.visibility !== "shared") return false;
      if (viewMode === "personal" && (c.visibility !== "private" || c.owner_id !== profile?.id)) return false;
    }
    if (filterLevel === "parent" && c.parent_id) return false;
    if (filterLevel === "child" && !c.parent_id) return false;
    return true;
  }
  const filteredCats = cats.filter(matchFilters);
  // Always show parent rows for any matching child so context is visible
  const childrenByParent = new Map<string, any[]>();
  const parentIds = new Set<string>();
  filteredCats.forEach((c: any) => {
    if (c.parent_id) {
      const arr = childrenByParent.get(c.parent_id) ?? [];
      arr.push(c); childrenByParent.set(c.parent_id, arr);
      parentIds.add(c.parent_id);
    } else {
      parentIds.add(c.id);
    }
  });
  const parents = Array.from(parentIds).map((id) => parentMap.get(id)).filter(Boolean);
  // Solo muestra padres que coincidan con el scope elegido (hogar→shared, personal→privado mío)
  const parentOptionsClean = cats.filter((c: any) => {
    if (c.parent_id) return false;
    if (isAdjustCategory(c)) return false;
    if (c.type !== type) return false;
    if (c.id === editingId) return false;
    if (scopeKey === "household" && c.visibility !== "shared") return false;
    if (scopeKey !== "household" && (c.visibility !== "private" || c.owner_id !== scopeKey)) return false;
    return true;
  });
  const members = memQ.data ?? [];
  const archivedFiltered = (archivedCatQ.data ?? []).filter((c: any) => {
    if (!isCouple) return true;
    if (viewMode === "household") return c.visibility === "shared";
    return c.visibility === "private" && c.owner_id === profile?.id;
  });

  // Resolve inherited scope for display when parent is selected
  const selectedParent: any = parentId !== "none" ? cats.find((c: any) => c.id === parentId) : null;
  const inheritedFromParent = !!selectedParent;
  const inheritedLabel = selectedParent
    ? selectedParent.visibility === "shared"
      ? "🏠 Hogar (compartida)"
      : `🧍 Personal de ${memberMap.get(selectedParent.owner_id)?.display_name ?? "—"}`
    : "";

  // Detect cascade when editing a parent and changing scope
  const currentScopeKey = scopeKey;
  const origScopeKey: string =
    origVisibility === "private" && origOwnerId ? origOwnerId : "household";
  const scopeChangedOnParent =
    !!editingId && editingIsParent && parentId === "none" && currentScopeKey !== origScopeKey;

  // Warning: esta sería la primera subcategoría de un padre que ya tiene movimientos
  const isFirstSubcategoryOfParent = parentId !== "none" && !hasChildrenSet.has(parentId);
  const showOrphanWarning =
    isFirstSubcategoryOfParent && (parentTxCountQ.data ?? 0) > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Categorías y Etiquetas</h1>
        <Button onClick={openNew}><Plus className="mr-2 h-4 w-4" /> Nueva categoría</Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card><CardHeader><CardTitle>Categorías</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            <div className="mb-3">
              <div className="grid grid-cols-2 gap-2">
                <Select value={filterType} onValueChange={(v: any) => setFilterType(v)}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los tipos</SelectItem>
                    <SelectItem value="income">Ingresos</SelectItem>
                    <SelectItem value="expense">Gastos</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterLevel} onValueChange={(v: any) => setFilterLevel(v)}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los niveles</SelectItem>
                    <SelectItem value="parent">Solo principales</SelectItem>
                    <SelectItem value="child">Solo subcategorías</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {parents.map((c: any) => (
              <div key={c.id}>
                <CategoryRow c={c} memberMap={memberMap} onEdit={openEdit} onDeactivate={deactivateCat} />
                {(childrenByParent.get(c.id) ?? []).map((child: any) => (
                  <div key={child.id} className="ml-6 flex items-start gap-1">
                    <CornerDownRight className="mt-3 h-3 w-3 text-muted-foreground" />
                    <div className="flex-1">
                      <CategoryRow c={child} memberMap={memberMap} onEdit={openEdit} onDeactivate={deactivateCat} parentName={c.name} />
                    </div>
                  </div>
                ))}
              </div>
            ))}
            {parents.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">Sin categorías que coincidan con los filtros.</div>}
            <div className="mt-4 border-t pt-3">
              <button
                type="button"
                onClick={() => setShowSystemCats((v) => !v)}
                className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <RotateCcw className="h-3 w-3" />
                {showSystemCats ? "Ocultar categorías del sistema" : "Ver categorías del sistema (⚙️ Ajuste de saldo)"}
              </button>
            </div>
            {(archivedCountQ.data ?? 0) > 0 && (
              <div className="mt-4 border-t pt-3">
                <button
                  type="button"
                  onClick={() => setShowArchived((v) => !v)}
                  className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <RotateCcw className="h-3 w-3" />
                  {showArchived ? "Ocultar archivadas" : `Ver archivadas (${archivedCountQ.data})`}
                </button>
                {showArchived && (
                  <div className="mt-2">
                    {archivedFiltered.length === 0 ? (
                      <p className="py-4 text-center text-xs text-muted-foreground">No hay categorías archivadas en esta vista.</p>
                    ) : (
                      archivedFiltered.map((c: any) => (
                        <CategoryRow key={c.id} c={c} memberMap={memberMap} onEdit={() => {}} onDeactivate={() => {}} onReactivate={reactivateCat} />
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card><CardHeader><CardTitle>Etiquetas</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="flex gap-2">
              <Input placeholder="Nueva etiqueta" value={tagName} onChange={(e) => setTagName(e.target.value)} />
              <Button onClick={addTag} disabled={savingTag || !tagName.trim()}>{savingTag ? "Añadiendo…" : "Añadir"}</Button>
            </div>
            {tagQ.data?.map((t) => (
              <div key={t.id} className="flex items-center justify-between border-b py-2">
                <span>{t.name}</span>
                <Button size="icon" variant="ghost" onClick={() => deleteTag(t.id)}><EyeOff className="h-4 w-4" /></Button>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Editar categoría" : "Nueva categoría"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div><Label>Tipo</Label>
              <Select value={type} onValueChange={(v: any) => setType(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="expense">Gasto</SelectItem>
                  <SelectItem value="income">Ingreso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>¿Para quién es esta categoría?</Label>
              {inheritedFromParent ? (
                <div className="mt-1 rounded-md border bg-muted/40 px-3 py-2 text-sm">
                  Hereda del padre: <b>{inheritedLabel}</b>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Las subcategorías heredan estrictamente la visibilidad y el dueño de su categoría principal.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mb-2">
                    Podés dejarla configurada como personal aunque por ahora seas el único miembro; cuando tu pareja se sume, ya estará lista.
                  </p>
                  <div className="flex rounded-md border p-0.5 mt-1">
                    <button
                      type="button"
                      onClick={() => handleScopeChange("household")}
                      className={cn(
                        "flex flex-1 items-center justify-center gap-1.5 rounded py-1.5 text-xs font-medium transition-colors",
                        scopeKey === "household" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      <Users className="h-3 w-3" /> Hogar
                    </button>
                    {profile && (
                      <button
                        type="button"
                        onClick={() => handleScopeChange(profile.id)}
                        className={cn(
                          "flex flex-1 items-center justify-center gap-1.5 rounded py-1.5 text-xs font-medium transition-colors",
                          scopeKey !== "household" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        <User className="h-3 w-3" /> Personal
                      </button>
                    )}
                  </div>
                  {scopeChangedOnParent && (
                    <div className="mt-2 flex items-start gap-2 rounded-md border border-warning/30 bg-[var(--warning-bg)] px-3 py-2 text-xs text-warning">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>
                        Este cambio se aplicará también a sus {childCountOfEditing} subcategoría{childCountOfEditing === 1 ? "" : "s"}.
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
            <div><Label>Subcategoría de (opcional)</Label>
              <Select value={parentId} onValueChange={setParentId} disabled={editingIsParent}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ninguna (categoría principal)</SelectItem>
                  {parentOptionsClean.map((p: any) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {editingIsParent && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Esta categoría es principal y tiene {childCountOfEditing} subcategoría{childCountOfEditing === 1 ? "" : "s"}; no puede convertirse en sub.
                </p>
              )}
              {showOrphanWarning && (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-warning/30 bg-[var(--warning-bg)] px-3 py-2 text-xs text-warning">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    Esta categoría ya tiene {parentTxCountQ.data} movimiento{parentTxCountQ.data === 1 ? "" : "s"} registrado{parentTxCountQ.data === 1 ? "" : "s"}. Al agregarle la primera subcategoría, esos movimientos quedarán sin subcategoría asignada. Podés reclasificarlos más adelante desde el listado de movimientos.
                  </span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Color</Label><Input type="color" value={color} onChange={(e) => setColor(e.target.value)} /></div>
              <div><Label>Icono (Lucide)</Label><Input value={icon} onChange={(e) => setIcon(e.target.value)} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveCategory} disabled={saving || !name.trim()}>
              {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CategoryRow({ c, memberMap, onEdit, onDeactivate, onReactivate, parentName }: { c: any; memberMap: Map<string, { id: string; display_name: string }>; onEdit: (c: any) => void; onDeactivate: (id: string) => void; onReactivate?: (id: string) => void; parentName?: string }) {
  const ownerName = c.owner_id ? memberMap.get(c.owner_id)?.display_name : null;
  const isChild = !!c.parent_id;
  const isAdjust = isAdjustCategory(c);
  return (
    <div className="flex items-center justify-between border-b py-2">
      <div className="flex flex-col gap-1">
        <span className="flex items-center gap-2">
          <span className="inline-block h-3 w-3 rounded-full" style={{ background: c.color }} />
          <span className={isChild ? "text-sm" : "font-semibold"}>{c.name}</span>
        </span>
        <span className="flex flex-wrap items-center gap-1">
          <Badge variant={c.type === "income" ? "default" : "secondary"} className="text-[10px]">
            {c.type === "income" ? "Ingreso" : "Gasto"}
          </Badge>
          {c.visibility === "private" ? (
            <Badge variant="outline" className="text-[10px] flex items-center gap-1">
              <Lock className="h-3 w-3" /> Personal de {ownerName ?? "—"}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[10px]">🏠 Hogar</Badge>
          )}
          {isChild && parentName && (
            <Badge variant="outline" className="text-[10px]">Sub de {parentName}</Badge>
          )}
          {!isChild && (
            <Badge variant="outline" className="text-[10px]">Principal</Badge>
          )}
          {isAdjust && (
            <Badge variant="outline" className="text-[10px]">⚙️ Sistema</Badge>
          )}
        </span>
      </div>
      <div className="flex gap-1">
        {onReactivate ? (
          <Button size="sm" variant="ghost" onClick={() => onReactivate(c.id)} title="Reactivar categoría">
            <RotateCcw className="h-4 w-4" />
          </Button>
        ) : isAdjust ? (
          <span className="px-2 text-[10px] text-muted-foreground self-center">No editable</span>
        ) : (
          <>
            <Button size="icon" variant="ghost" onClick={() => onEdit(c)} title="Editar"><Pencil className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" onClick={() => onDeactivate(c.id)} title="Desactivar (conserva el historial)">
              <EyeOff className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
